/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { CompaniesAction } from 'store/companies/CompaniesAction'
import { Company } from 'types/entities/Company'
import { Reservation } from 'types/entities/Reservation'
import { AppThunkDispatch } from 'helpers/store'
import { resolveReservationContact } from 'utils/reservations/reservationDisplayUtils'

const needsCompanyEnrichment = (reservation: Reservation): boolean => {
    if (!reservation.company_id) {
        return false
    }

    if (!reservation.company) {
        return true
    }

    return !reservation.company.name?.trim() && !(reservation.company.contacts?.length ?? 0)
}

export const enrichReservationsWithCompanies = async (
    reservations: Reservation[],
    dispatch: AppThunkDispatch
): Promise<Reservation[]> => {
    const companyIds = [
        ...new Set(
            reservations
                .filter(needsCompanyEnrichment)
                .map((reservation) => reservation.company_id)
                .filter((companyId): companyId is number => typeof companyId === 'number')
        ),
    ]

    if (companyIds.length === 0) {
        return reservations.map((reservation) => ({
            ...reservation,
            contact: reservation.contact ?? resolveReservationContact(reservation),
        }))
    }

    const companyEntries = await Promise.all(
        companyIds.map(async (companyId) => {
            try {
                const company = await dispatch(CompaniesAction.fetchCompanyByIdRaw(companyId))
                return [companyId, company] as const
            } catch {
                return null
            }
        })
    )

    const companyById = new Map<number, Company>(
        companyEntries.filter((entry): entry is readonly [number, Company] => entry !== null)
    )

    return reservations.map((reservation) => {
        const company =
            reservation.company_id != null
                ? companyById.get(reservation.company_id) ?? reservation.company
                : reservation.company

        const enrichedReservation: Reservation = {
            ...reservation,
            company: company ?? reservation.company ?? null,
        }

        return {
            ...enrichedReservation,
            contact: enrichedReservation.contact ?? resolveReservationContact(enrichedReservation),
        }
    })
}
